import type { MouseEventHandler, ReactElement } from 'react'
import React, { useCallback, useContext } from 'react'
import { useIntl } from 'react-intl'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import Clickable from 'components/Common/Clickable'
import { searchResultPolicyClick } from 'analytics/eventDefinitions'
import { addGTMEvent } from 'api/googleTagManager'
import BedbankCancellationPolicyModal from './BedbankCancellationPolicyModal'
import BedbankPropertyCancellationPoliciesBase from 'components/Common/CancellationPolicies/Bedbank/BedbankPropertyCancellationPoliciesBase'
import BodyTextBlock from 'LuxKit/TextBlocks/BodyTextBlock'
import ModalContext from 'contexts/ModalContext'
import type { BedbankCancellationPolicyWithInfo } from '../../../lib/search/hotels'
import InlineIcon from 'components/utils/InlineIcon'

interface Props {
  rate: App.BedbankRate | App.OrderBedbankRate | BedbankCancellationPolicyWithInfo
  showNonCancellation: boolean
  isConfirmation?: boolean
  showHotelOnly?: boolean
  icon?: ReactElement
}

function BedbankCancellationPolicy({
  rate,
  isConfirmation,
  showNonCancellation,
  showHotelOnly = true,
  icon,
}: Props) {
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const openModal = useCallback<MouseEventHandler<HTMLButtonElement>>((event) => {
    event.preventDefault()
    addGTMEvent(searchResultPolicyClick())
    const modalHeading = rate.partiallyRefundable ? intl.formatMessage({
      id: '5MIsi9WIE3If_SkiXbxP',
      defaultMessage: 'Partially refundable',
      description: 'Modal heading for partially refundable bedbank cancellation policy',
    }) :
      rate.refundable ? intl.formatMessage({
        id: '2bNqBX95lob5QLgpI0E7',
        defaultMessage: 'Free cancellation',
        description: 'Modal heading for free cancellation bedbank cancellation policy',
      }) :
          intl.formatMessage({
            id: 'Jlgos6TltckjqA3hBoAL',
            defaultMessage: 'Non-refundable and no date changes permitted',
            description: 'Modal heading for non-refundable bedbank cancellation policy',
          })

    showModal(<BedbankCancellationPolicyModal
      heading={modalHeading}
      policyDescriptions={rate.cancellationPolicy.description ?? []}
    />)
  }, [rate, showModal, intl])

  const shouldShowCancellationPolicy = rate.partiallyRefundable || (rate.refundable && !isConfirmation) || (!rate.partiallyRefundable && !rate.refundable && showNonCancellation)

  if (!shouldShowCancellationPolicy) return null

  return <Clickable onClick={openModal}>
    {rate.partiallyRefundable && <BodyTextBlock variant="medium" startIcon={icon}>
      {intl.formatMessage({
        id: '5MIsi9WIE3If_SkiXbxP',
        defaultMessage: 'Partially refundable',
        description: 'Modal heading for partially refundable bedbank cancellation policy',
      })} <InlineIcon Icon={LineInfoCircleIcon} />
    </BodyTextBlock>}
    {rate.refundable && !isConfirmation && <BedbankPropertyCancellationPoliciesBase icon={icon} cancellationPolicy={rate.cancellationPolicy} showHotelOnly={showHotelOnly} />}
    {!rate.partiallyRefundable && !rate.refundable && showNonCancellation && <BodyTextBlock variant="medium" startIcon={icon}>
      {intl.formatMessage({
        id: 'Q5qCA0aaEKJjeqGUYIjn',
        defaultMessage: 'Non-refundable',
        description: 'Non-refundable text for bedbank cancellation policy',
      })} <InlineIcon Icon={LineInfoCircleIcon} />
    </BodyTextBlock>}
  </Clickable>
}

export default BedbankCancellationPolicy
